import React, { useEffect, useState, useMemo, useCallback, FormEvent } from 'react';
import api from './../../services/api';
import { useAuth } from './../../hooks/auth';
import { getApiErrorMessage } from '../../utils/authRedirect';
import {
	DAILY_VISIT_LIMIT_MESSAGE,
	CountableVisitAppointment,
	filterRescheduleSlotsByDailyVisitLimit,
	getScheduleApiErrorMessage,
} from '../../utils/dailyVisitLimit';
import { Container, Content, TitleContainer, Form, SelectContainer, ErrorAlert } from './styles';
import { FiX } from 'react-icons/fi';
import Select from 'react-select';

interface IModalReSchedule {
	consultantID: number;
	propertyID: number;
	scheduleID: number;
	fromDate?: string;
	functionToCloseModalReSchedule(): void;
}

interface IDates {
	id: number;
	date: string;
	consultant_id: number;
	booked_count?: number;
	max_capacity?: number;
	status: number;
}

interface ISelectOptions {
	label: string;
	value: string;
}

export default function ModalReSchedule({ consultantID, propertyID, scheduleID, fromDate, functionToCloseModalReSchedule }: IModalReSchedule) {
	const { access_token } = useAuth();

	const [dates, setDates] = useState<IDates[]>([]);
	const [userAppointments, setUserAppointments] = useState<CountableVisitAppointment[]>([]);
	const [hours, setHours] = useState<ISelectOptions[]>([]);
	const [selectedDate, setSelectedDate] = useState("");
	const [selectedHour, setSelectedHour] = useState("");
	const [error, setError] = useState('');
	const [errorInSelectedDate, setErrorInSelectedDate] = useState(false);
	const [errorInSelectedHour, setErrorInSelectedHour] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadingApiRequest, setLoadingApiRequest] = useState(false);

	const hasAvailableSlots = useCallback((schedule: IDates): boolean => {
		if (schedule.booked_count !== undefined && schedule.max_capacity !== undefined) {
			return schedule.booked_count < schedule.max_capacity;
		}
		return schedule.status === 1 || schedule.status === 2;
	}, []);

	useEffect(() => {
		if (access_token) {
			api.defaults.headers.authorization = `Bearer ${access_token}`;
		}

		Promise.all([
			api.get(`/api/client/schedules/consultant/${consultantID}/property/${propertyID}`),
			api.get('/api/client/schedules/user/appointments').catch(() => ({ data: { data: [] } })),
		])
			.then(([slotsResponse, appointmentsResponse]) => {
				setDates(slotsResponse.data.data);
				setUserAppointments(appointmentsResponse.data.data || []);
				setLoading(false);
			})
			.catch(err => {
				setError(getApiErrorMessage(err, 'Erro ao carregar horários disponíveis'));
				setLoading(false);
			});
	}, [consultantID, propertyID, access_token]);

	const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoadingApiRequest(true);
		setError('');

		try {
			if (selectedDate === "") {
				setLoadingApiRequest(false);
				return setErrorInSelectedDate(true);
			}

			if (selectedHour === "") {
				setLoadingApiRequest(false);
				return setErrorInSelectedHour(true);
			}

			const getNewSchedule = dates.find(date => {
				const matchesDateTime = date.date === `${selectedDate} ${selectedHour}`;
				return matchesDateTime && hasAvailableSlots(date);
			});

			if (!!getNewSchedule) {
				const campaign = localStorage.getItem('@7Cantos:Campaign');
				const channel = localStorage.getItem('@7Cantos:Channel');

				api.defaults.headers.authorization = `Bearer ${access_token}`;

				const response = await api.put('/api/client/schedules/reschedule', {
					from_schedule_id: scheduleID,      
					to_schedule_id: getNewSchedule.id, 
					property_id: propertyID,
					video_call: false,                 
					campaign: campaign || "",          
					userChannel: ""                    
				});

				if (response.data.success) {
					functionToCloseModalReSchedule();
					if (campaign) {
						localStorage.removeItem('@7Cantos:Campaign');
					}
				} else {
					setError(response.data.message || 'Erro no reagendamento');
				}
			} else {
				setError('Horário não encontrado ou já lotado');
			}

			setLoadingApiRequest(false);
			setErrorInSelectedDate(false);
			setErrorInSelectedHour(false);
		} catch (err: any) {
			console.log("Erro no submit:", err);
			setError(getScheduleApiErrorMessage(err, 'Erro ao processar reagendamento'));
			setLoadingApiRequest(false);
		}
	}, [selectedDate, selectedHour, dates, propertyID, scheduleID, access_token, hasAvailableSlots, functionToCloseModalReSchedule]);

	const datesWithinDailyLimit = useMemo(() => {
		const availableDates = dates.filter(date => hasAvailableSlots(date));
		return filterRescheduleSlotsByDailyVisitLimit(
			availableDates,
			userAppointments,
			propertyID,
			fromDate
		);
	}, [dates, hasAvailableSlots, userAppointments, propertyID, fromDate]);

	const handleSelectDate = useCallback((selectedDate: string) => {
		if (selectedDate === "") {
			setSelectedDate("");
			setSelectedHour("");
			setHours([]);
			return;
		}

		const formatDate = `${selectedDate.substring(6, 10)}-${selectedDate.substring(3, 5)}-${selectedDate.substring(0, 2)}`;

		const getHours = datesWithinDailyLimit.filter(date => (
			date.date.includes(formatDate) && hasAvailableSlots(date)
		));

		const orderHours = getHours.sort((a, b) => {
			return Number(a.date.substring(11, 13)) - Number(b.date.substring(11, 13));
		});

		const formatHours = orderHours.map(hour => ({
			label: hour.date.substr(11, 19),
			value: hour.date.substr(11, 19)
		}));

		setSelectedDate(formatDate);
		setHours(formatHours);
		setSelectedHour("");
	}, [datesWithinDailyLimit, hasAvailableSlots]);

	const handleSelectHour = useCallback((hour: string) => {
		if (hour === "") {
			return setSelectedHour("");
		}
		if (!!selectedDate) {
			setSelectedHour(hour);
		}
	}, [selectedDate]);

	const dailyLimitBlockedAllDates =
		dates.some((date) => hasAvailableSlots(date)) &&
		datesWithinDailyLimit.length === 0;

	const datesFilter = useMemo(() => {
		const getDates = datesWithinDailyLimit.map(date => date.date.substring(0, 10));
		const uniques = [...new Set(getDates)];
		const orderDates = uniques.sort((a, b) => (+new Date(a) - +new Date(b)));

		const buildDate = orderDates.map(date => ({
			label: `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(0, 4)}`,
			value: `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(0, 4)}`
		}));

		return buildDate;
	}, [datesWithinDailyLimit]);

	return (
		<Container>
			<Content>
				<TitleContainer>
					<button onClick={functionToCloseModalReSchedule}><FiX size={30} color="#FFF" /></button>
					<h1>REAGENDAMENTO</h1>
				</TitleContainer>

				<Form onSubmit={handleSubmit}>
					<SelectContainer>
						<p>Data de preferência: {!!errorInSelectedDate ? <span className="error-text">Escolha uma data</span> : ""}</p>
						<Select
							options={datesFilter}
							onChange={e => handleSelectDate(e!.value)}
							placeholder={!!loading ? "Carregando..." : "Escolha uma data"}
							noOptionsMessage={() => (!!loading
								? 'Carregando...'
								: dailyLimitBlockedAllDates
									? DAILY_VISIT_LIMIT_MESSAGE
									: 'Não temos datas disponíveis')}
							isDisabled={loading}
						/>
					</SelectContainer>

					<SelectContainer>
						<p>Hora de preferência: {!!errorInSelectedHour ? <span className="error-text">Escolha uma hora</span> : ""}</p>
						<Select
							options={hours}
							onChange={e => handleSelectHour(e!.value)}
							placeholder={selectedDate ? "Escolha um horário" : "Primeiro escolha uma data"}
							noOptionsMessage={() => ('Não temos horários disponíveis nessa data.')}
							isDisabled={!selectedDate || hours.length === 0}
						/>
					</SelectContainer>

					{error && (
						<ErrorAlert>
							{error}
						</ErrorAlert>
					)}

					{!loadingApiRequest ? (
						<>
							<button type="submit" className={`submit-button ${error ? 'error-button' : 'success-button'}`}>
								{!!error ? 'TENTAR NOVAMENTE' : "REAGENDAR"}
							</button>
							<button type="button" onClick={functionToCloseModalReSchedule} className="cancel-button">
								CANCELAR
							</button>
						</>
					) : (
						<label>Carregando...</label>
					)}
				</Form>
			</Content>
		</Container>
	);
};